import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import sandeepImage from "../assests/sandeep.webp";
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Award,
  Users,
  HandHeart,
  MessagesSquare,
  BadgeCheck,
  UserCheck,
  Clock,
  Star,
  Trophy,
  MapPin,
  Phone,
  Mail,
  Building2,
} from "lucide-react";
import {
  SERVICES,
  CREDENTIALS,
  ADVISOR,
  BLOG_POSTS,
  TESTIMONIALS,
} from "../data/mock";
import { useRevealGroup, useCounter } from "../hooks/useReveal";
import {
  SectionHeading,
  PillBadge,
  ServiceCard,
  CTASection,
  ScheduleCallButton,
  TestimonialsSection,
} from "../components/Shared";
import { useQuote } from "../components/QuoteModal";

const HOME_PHONE = "(604) 996 6862";
const HOME_PHONE_HREF = "tel:+16049966862";
const HOME_EMAIL = "info@abhifinancial.com";

const HOME_MAP_URL =
  "https://www.google.com/maps/dir/?api=1&destination=8128%20128%20St%20%23302%2C%20Surrey%2C%20BC%20V3W%201R1%2C%20Canada";

const HOME_TITLE =
  "Insurance Agency Surrey BC | Super Visa and Life Insurance | Abhi Insurance";

const HOME_META_DESCRIPTION =
  "Licensed insurance advisor in Surrey BC. Super Visa, visitor, life, critical illness and disability coverage. Free consultations 7 days a week. Call (604) 996 6862.";

const HOME_DISCLAIMER =
  "Abhi Insurance Agency Inc. is a licensed insurance advisory based in Surrey, British Columbia. The information on this page is general in nature and is not financial, legal or insurance advice. Coverage terms, eligibility, premiums and program requirements vary by insurer and by individual circumstances, and change over time. Nothing here guarantees eligibility, approval or a particular premium. Speak with a licensed advisor before making any insurance decision, and refer to your policy documents for the terms that actually apply to you.";

  const MOBILE_PAGE_DISCLAIMER =
  "This information is general only and is not financial, legal or insurance advice. Coverage, eligibility and premiums vary. Speak with a licensed advisor before making an insurance decision.";

// Home-only vertical spacing.
// Horizontal margins, widths and padding remain unchanged.
const HOME_SPACING_CSS = `
  #abhi-home-page > .home-about-section {
    padding-bottom: 32px !important;
  }

  #abhi-home-page > .home-services-section {
    padding-top: 32px !important;
  }

  #abhi-home-page > .home-advisor-section {
    padding-bottom: 32px !important;
  }

  #abhi-home-page > .home-testimonials-section {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    padding-top: 32px !important;
    padding-bottom: 32px !important;
  }

  #abhi-home-page > .home-location-section {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    padding-top: 32px !important;
    padding-bottom: 32px !important;
  }

  #abhi-home-page > .home-protection-section {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  #abhi-home-page > .home-blog-section {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    padding-top: 32px !important;
    padding-bottom: 32px !important;
  }

  #abhi-home-page > .home-final-cta-wrap {
    margin-top: 0 !important;
    padding-top: 0 !important;
  }

  #abhi-home-page > .home-final-cta-wrap > section {
    margin-top: 0 !important;
    padding-top: 24px !important;
  }

  @media (max-width: 1023px) {
    /*
     * Reduce the gap between testimonials and location.
     */
    #abhi-home-page > .home-testimonials-section {
      padding-bottom: 20px !important;
    }

    #abhi-home-page > .home-location-section {
      padding-top: 20px !important;
    }

    #abhi-home-page > .home-location-section > div {
      gap: 28px !important;
    }

    /*
     * Center testimonial controls.
     */
    #abhi-home-page
      .home-testimonials-section
      .testimonials-navigation {
      justify-content: center !important;
      width: 100%;
    }

    #abhi-home-page
      .home-testimonials-section
      .testimonials-controls {
      justify-content: center !important;
      margin-inline: auto !important;
    }

    /*
     * Center testimonial quote icon.
     */
    #abhi-home-page
      .home-testimonials-section
      .testimonials-quote-mark {
      display: block !important;
      margin-left: auto !important;
      margin-right: auto !important;
    }

    /*
     * Center testimonial author.
     */
    #abhi-home-page
      .home-testimonials-section
      .testimonials-author {
      justify-content: center !important;
      width: 100%;
    }

    #abhi-home-page
      .home-testimonials-section
      .testimonials-author > div {
      text-align: left !important;
    }

    /*
     * Center testimonial dots.
     */
    #abhi-home-page
      .home-testimonials-section
      .testimonials-pagination {
      justify-content: center !important;
      width: 100%;
    }

    /*
     * Center the main Our Location content on mobile.
     */
    #abhi-home-page .home-location-intro {
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
      transform: none !important;
      text-align: center !important;
    }

    #abhi-home-page .home-location-heading-icon {
      margin-left: auto !important;
      margin-right: auto !important;
    }

    /*
     * Put Users icon above its paragraph.
     */
    #abhi-home-page .home-location-audience {
      flex-direction: column !important;
      align-items: center !important;
      text-align: center !important;
    }

    #abhi-home-page .home-location-audience-icon {
      margin-top: 0 !important;
    }

    /*
     * Keep all location-card icons in one left column.
     */
    #abhi-home-page .home-location-card-header {
      display: grid !important;
      grid-template-columns: 44px minmax(0, 1fr) 16px !important;
      align-items: center !important;
      column-gap: 16px !important;
      text-align: left !important;
    }

    #abhi-home-page .home-location-card-header h3 {
      grid-column: 2 / 4 !important;
      text-align: left !important;
    }

    #abhi-home-page .home-location-address-row {
      display: grid !important;
      grid-template-columns: 44px minmax(0, 1fr) 16px !important;
      align-items: center !important;
      column-gap: 16px !important;
      text-align: left !important;
    }

    #abhi-home-page .home-location-address-row p {
      grid-column: 2 / 4 !important;
      text-align: left !important;
    }

    #abhi-home-page .home-location-contact-row {
      display: grid !important;
      grid-template-columns: 44px minmax(0, 1fr) 16px !important;
      align-items: center !important;
      column-gap: 16px !important;
      text-align: left !important;
    }

    #abhi-home-page
      .home-location-contact-row
      > span:nth-child(2) {
      text-align: left !important;
    }

    #abhi-home-page .home-location-contact-row > svg {
      grid-column: 3 !important;
      margin-left: auto !important;
    }

    /*
     * Same spacing between phone/email as location/phone.
     */
    #abhi-home-page
      .home-location-contact-row
      + .home-location-contact-row {
      margin-top: 24px !important;
      padding-top: 24px !important;
      border-top: 1px solid #e1ecf3 !important;
    }

    #abhi-home-page .home-location-hours {
      justify-content: flex-start !important;
      text-align: left !important;
    }
  }

  @media (min-width: 1024px) {
    #abhi-home-page > .home-about-section {
      padding-bottom: 48px !important;
    }

    #abhi-home-page > .home-services-section {
      padding-top: 48px !important;
    }

    #abhi-home-page > .home-advisor-section {
      padding-bottom: 48px !important;
    }

    #abhi-home-page > .home-testimonials-section {
      padding-top: 48px !important;
      padding-bottom: 48px !important;
    }

    #abhi-home-page > .home-location-section {
      padding-top: 48px !important;
      padding-bottom: 48px !important;
    }

    /*
     * Force left location content to remain visible on desktop.
     */
    #abhi-home-page .home-location-intro {
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
      transform: none !important;
      text-align: left !important;
    }

    #abhi-home-page > .home-blog-section {
      padding-top: 48px !important;
      padding-bottom: 48px !important;
    }

    #abhi-home-page > .home-final-cta-wrap > section {
      padding-top: 24px !important;
    }
  }
`;
const WHY_ICONS = {
  MessagesSquare,
  BadgeCheck,
  UserCheck,
  Clock,
  Star,
  Trophy,
};

const HOME_SERVICE_IMAGES = {
  "super-visa-insurance":
    "https://images.pexels.com/photos/3768131/pexels-photo-3768131.jpeg?auto=compress&cs=tinysrgb&w=1200",
  "visitor-visa-insurance":
    "https://images.pexels.com/photos/723240/pexels-photo-723240.jpeg?auto=compress&cs=tinysrgb&w=1200",
  "life-insurance":
    "https://images.pexels.com/photos/1128318/pexels-photo-1128318.jpeg?auto=compress&cs=tinysrgb&w=1200",
  "critical-illness-insurance":
    "https://images.pexels.com/photos/5452201/pexels-photo-5452201.jpeg?auto=compress&cs=tinysrgb&w=1200",
  "disability-insurance":
    "https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&w=1200",
};

const HOME_SERVICE_DESCRIPTIONS = {
  "super-visa-insurance":
    "Mandatory medical coverage for parents and grandparents visiting Canada on the Super Visa program. Minimum $100,000 from a Canadian insurer, with monthly payment options available.",
  "visitor-visa-insurance":
    "Emergency medical protection for anyone visiting Canada, whether for a short trip, an extended stay, or while waiting for provincial health coverage to begin.",
  "life-insurance":
    "Term, whole and permanent policies compared side by side, so your family keeps their home, their lifestyle and their peace of mind.",
  "critical-illness-insurance":
    "A lump sum benefit on diagnosis of a covered illness, so you can focus on recovery instead of medical bills and lost income.",
  "disability-insurance":
    "Income protection if illness or injury keeps you from working, replacing a portion of your monthly income for as long as you need it.",
};

const HOME_SERVICES = SERVICES.map((service) => ({
  ...service,
  short: HOME_SERVICE_DESCRIPTIONS[service.slug] ?? service.short,
}));

const HOME_STATS = [
  {
    value: 20,
    suffix: "+",
    label: "Years of Trusted Service",
  },
  {
    value: 45,
    prefix: "Up to",
    suffix: "%",
    label: "Premium Savings",
  },
  {
    value: 8,
    suffix: "",
    label: "Times Top of the Table",
  },
  {
    value: 7,
    suffix: "",
    label: "Days a Week Availability",
  },
];

const HOME_FEATURES = [
  "Comprehensive protection built around your circumstances",
  "Plans compared across Canada's top insurers",
  "Fast claims support and same day partial refunds",
  "Up to 45% discounted rates with guaranteed lowest premiums",
];

const HOME_WHY_CHOOSE = [
  {
    icon: "MessagesSquare",
    title: "Free Advice Consultations",
    text: "Speak with a licensed advisor by phone or in person, completely free, with no obligation.",
  },
  {
    icon: "BadgeCheck",
    title: "No Hidden Fees",
    text: "Total transparency from your first consultation to your final policy. What we quote is what you pay.",
  },
  {
    icon: "UserCheck",
    title: "In House Claim Assistance",
    text: "A dedicated claim assistance manager works directly with clients when you need support most.",
  },
  {
    icon: "Clock",
    title: "Same Day Partial Refunds",
    text: "Partial refunds are processed the very same day. Your money moves as fast as you do.",
  },
  {
    icon: "Star",
    title: "5 Star Rated Service",
    text: "Consistently rated 5 stars by clients across Surrey and the Lower Mainland.",
  },
  {
    icon: "Trophy",
    title: "Industry Recognition",
    text: "Top of the Table 8 times, MDRT member since 2010, Advocis member with CHS designation.",
  },
];

const HOME_ADVISOR = {
  ...ADVISOR,
  image: sandeepImage,
  name: "Sandeep Ahuja",
  role: "Founder and Licensed Insurance Advisor",
  bio: "Sandeep Ahuja has been advising Canadians, newcomers and visitors on insurance since 2000, and founded Abhi Insurance Agency in 2009. A fully licensed insurance advisor, MDRT member since 2010 and 8 time Top of the Table designee, Sandeep compares the benefits and drawbacks of plans across companies so every client is confident they hold the best possible coverage for their needs and their budget.",
};

const HOME_TESTIMONIALS = TESTIMONIALS.map((item) =>
  item.name === "Doug Ramsey"
    ? {
        ...item,
        role: "Criminal Lawyer",
        text: "I am so pleased and satisfied with this insurance company's service. It is so great to work with an adviser who is truly interested in their client's needs, goals and preferences. I am really impressed with their commitment.",
      }
    : item
);

function useHomeMetadata() {
  useEffect(() => {
    const previousTitle = document.title;
    const existingDescription = document.head.querySelector(
      'meta[name="description"]'
    );
    const previousDescription =
      existingDescription?.getAttribute("content");

    const description =
      existingDescription ?? document.createElement("meta");

    if (!existingDescription) {
      description.setAttribute("name", "description");
      document.head.appendChild(description);
    }

    document.title = HOME_TITLE;
    description.setAttribute("content", HOME_META_DESCRIPTION);

    return () => {
      document.title = previousTitle;

      if (!existingDescription) {
        description.remove();
      } else if (previousDescription === null) {
        description.removeAttribute("content");
      } else {
        description.setAttribute("content", previousDescription);
      }
    };
  }, []);
}

function StatItem({ stat, index }) {
  const [ref, value] = useCounter(stat.value);

  return (
    <div
      ref={ref}
      data-reveal
      style={{ transitionDelay: `${index * 90}ms` }}
      className="text-center lg:text-left"
    >
      <p className="font-heading text-4xl font-extrabold tracking-tight text-[#006FB9] sm:text-5xl">
        {stat.prefix && (
          <span className="mr-2 inline-block text-sm font-medium tracking-normal text-[#526A7D]">
            {stat.prefix}
          </span>
        )}
        {value}
        <span className="text-[#54AEDB]">{stat.suffix}</span>
      </p>

      <p className="mt-2 text-sm font-medium text-[#526A7D]">
        {stat.label}
      </p>
    </div>
  );
}

// Uses the redesigned shared component.
// The old local carousel and its outer-card markup are removed.
function HomeTestimonialsSection() {
  return (
    <TestimonialsSection
      testimonials={HOME_TESTIMONIALS}
      subtitle="Real words from the families and professionals we protect across British Columbia."
      className="home-testimonials-section"
    />
  );
}

function HomeLocationSection() {
  return (
    <section
      className="home-location-section bg-white"
      aria-labelledby="home-location-heading"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-6 lg:grid-cols-2 lg:gap-14">
        {/* Left-side location information */}
        <div className="home-location-intro min-w-0">
          <span className="home-location-heading-icon mb-5 grid h-14 w-14 place-items-center rounded-2xl bg-[#E2F2FA]">
            <MapPin
              aria-hidden="true"
              className="h-7 w-7 text-[#006FB9]"
            />
          </span>

          <h2
            id="home-location-heading"
            className="font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-[#0B2748] sm:text-4xl lg:text-[42px]"
          >
            Our <span className="text-[#0785C7]">Location</span>
          </h2>

          <p className="mt-5 text-[15.5px] leading-relaxed text-[#526A7D]">
            Abhi Insurance Agency meets all your insurance needs,
            including life insurance, visitor visa insurance, disability
            insurance and more.
          </p>

          <div className="home-location-audience mt-6 flex items-start gap-3 border-t border-[#E1ECF3] pt-6">
           <Users
  aria-hidden="true"
  className="home-location-audience-icon mt-1 h-6 w-6 shrink-0 text-[#0785C7]"
/>

            <p className="text-[15.5px] leading-relaxed text-[#526A7D]">
              We advise families across Newton, Fleetwood, Guildford,
              Cloverdale, South Surrey, Whalley and City Centre, as well as
              Delta, Langley, White Rock, Burnaby, Richmond, Coquitlam and
              Vancouver. Visit us in person, or handle everything by phone
              and email if that suits you better.
            </p>
          </div>
        </div>

        {/* Right-side office card */}
        <div
          data-reveal
          style={{ transitionDelay: "120ms" }}
          className="min-w-0 overflow-hidden rounded-3xl border border-[#E1ECF3] bg-white shadow-[0_12px_36px_rgba(6,59,103,0.08)]"
        >
          {/* Card heading */}
          <div className="home-location-card-header flex items-center gap-4 bg-[#073B67] px-6 py-6 sm:px-8">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-white/10">
              <Building2
                aria-hidden="true"
                className="h-6 w-6 text-[#B9DFF3]"
              />
            </span>

            <h3 className="font-heading text-lg font-bold leading-snug text-white sm:text-xl">
              Abhi Insurance Agency Inc.
            </h3>
          </div>

          <address className="not-italic">
  {/* Location, phone and email */}
  <div className="px-6 py-6 sm:px-8">
    {/* Location */}
    <a
      href={HOME_MAP_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Open Abhi Insurance Agency location in Google Maps"
      className="home-location-contact-row group flex items-center gap-4"
    >
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#E2F2FA] transition-colors group-hover:bg-[#006FB9]">
        <MapPin
          aria-hidden="true"
          className="h-5 w-5 text-[#006FB9] transition-colors group-hover:text-white"
        />
      </span>

      <span className="min-w-0">
        <span className="block text-xs font-medium text-[#526A7D]">
          Location:
        </span>

        <span className="mt-0.5 block text-[15.5px] font-semibold leading-relaxed text-[#006FB9] group-hover:underline">
          8128 128 St #302
        </span>

        <span className="block text-sm font-normal text-[#526A7D]">
          Surrey, BC V3W 1R1
        </span>
      </span>

      <ArrowUpRight
        aria-hidden="true"
        className="ml-auto h-4 w-4 shrink-0 text-[#006FB9]"
      />
    </a>

    {/* Phone */}
    <a
      href={HOME_PHONE_HREF}
      className="home-location-contact-row group mt-5 flex items-center gap-4"
    >
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#E2F2FA] transition-colors group-hover:bg-[#006FB9]">
        <Phone
          aria-hidden="true"
          className="h-5 w-5 text-[#006FB9] transition-colors group-hover:text-white"
        />
      </span>

      <span className="min-w-0">
        <span className="block text-xs font-medium text-[#526A7D]">
          Phone:
        </span>

        <span className="mt-0.5 block text-[15.5px] font-semibold text-[#006FB9] group-hover:underline">
          {HOME_PHONE}
        </span>
      </span>

      <ArrowUpRight
        aria-hidden="true"
        className="ml-auto h-4 w-4 shrink-0 text-[#006FB9]"
      />
    </a>

    {/* Email */}
    <a
      href={`mailto:${HOME_EMAIL}`}
      className="home-location-contact-row group mt-5 flex items-center gap-4"
    >
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#E2F2FA] transition-colors group-hover:bg-[#006FB9]">
        <Mail
          aria-hidden="true"
          className="h-5 w-5 text-[#006FB9] transition-colors group-hover:text-white"
        />
      </span>

      <span className="min-w-0">
        <span className="block text-xs font-medium text-[#526A7D]">
          Email:
        </span>

        <span className="mt-0.5 block break-words text-[15.5px] font-semibold text-[#006FB9] group-hover:underline">
          {HOME_EMAIL}
        </span>
      </span>

      <ArrowUpRight
        aria-hidden="true"
        className="ml-auto h-4 w-4 shrink-0 text-[#006FB9]"
      />
    </a>
  </div>

  {/* Business hours */}
  <div className="home-location-hours flex items-center gap-3 border-t border-[#E1ECF3] bg-[#F4F7FB] px-6 py-4 sm:px-8">
    <Clock
      aria-hidden="true"
      className="h-5 w-5 shrink-0 text-[#006FB9]"
    />

    <p className="text-sm font-medium leading-relaxed text-[#193B58]">
      Open 7 days a week, 9:00 to 17:00
    </p>
  </div>
</address>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const revealRef = useRevealGroup();
  const { openQuote } = useQuote();

  useHomeMetadata();

  return (
    <div ref={revealRef} id="abhi-home-page">
      <style>{HOME_SPACING_CSS}</style>

      {/* HERO */}
      <section className="home-hero relative overflow-hidden bg-[#F4F7FB]">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1531983412531-1f49a365ffed?q=80&w=1920"
            alt="Happy family on the beach"
            className="kenburns h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#062A4D]/85 via-[#073B67]/55 to-transparent" />
        </div>

        <div className="relative mx-auto max-w-7xl px-6 pb-20 pt-16 sm:pb-24 sm:pt-20 lg:pb-28 lg:pt-24">
          <div className="hero-stagger max-w-2xl">
            <PillBadge light>We Are Abhi Insurance</PillBadge>

            <h1 className="mt-5 font-heading text-5xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
              Protecting You{" "}
              <span className="block text-[#B9DFF3]">Always</span>
            </h1>

            <p className="mt-6 max-w-xl text-[16.5px] leading-relaxed text-[#D9ECF7]">
              Since 2000 we have helped Canadians, newcomers and visitors
              find the right coverage at the right price. Super Visa,
              visitor visa, life, critical illness and disability insurance,
              compared across Canada's leading insurers.
            </p>

            <div className="hero-actions mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={() => openQuote()}
                className="btn-light group"
                data-testid="hero-get-quote"
              >
                Get a Free Quote
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>

              <a
                href={HOME_PHONE_HREF}
                className="btn-outline-light group"
                data-testid="hero-call"
              >
                Talk to an Advisor
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>

            <div className="hero-trust-points mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[#D4E9F4]">
              <span className="flex items-center gap-2">
                <Award className="h-4 w-4 text-[#B9DFF3]" />
                8x 'Top of the Table'
              </span>
              <span className="flex items-center gap-2">
                <Users className="h-4 w-4 text-[#B9DFF3]" />
                MDRT Member since 2010
              </span>
              <span className="flex items-center gap-2">
                <HandHeart className="h-4 w-4 text-[#B9DFF3]" />
                100% Satisfaction Guaranteed
              </span>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#F4F7FB] to-transparent" />
      </section>

      {/* STATS */}
      <section className="bg-[#F4F7FB] py-16 lg:py-20">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2">
          <div data-reveal>
            <h2 className="font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-[#0B2748] sm:text-4xl">
              Unmatched service. Unwavering commitment.{" "}
              <span className="text-[#0785C7]">Always for you.</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 gap-8 lg:gap-10">
            {HOME_STATS.map((stat, index) => (
              <StatItem key={stat.label} stat={stat} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="home-about-section bg-white py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2">
          <div
            className="relative pb-16 sm:pb-12 lg:pb-8"
            data-reveal
          >
            <div className="overflow-hidden rounded-3xl shadow-[0_12px_36px_rgba(6,59,103,0.10)]">
             <img
  src="https://images.pexels.com/photos/3875141/pexels-photo-3875141.jpeg?auto=compress&cs=tinysrgb&w=1200"
  alt="Happy family enjoying time together in their living room"
  className="h-[440px] w-full object-cover object-center transition-transform duration-700 hover:scale-105 sm:h-[520px] lg:h-[560px]"
/>
            </div>

            <div className="absolute bottom-2 right-3 w-[180px] rounded-2xl border-4 border-white bg-[#006FB9] p-5 text-white shadow-[0_20px_50px_rgba(0,111,185,0.28)] sm:-right-3 sm:bottom-0 sm:w-[205px] sm:p-6 lg:-right-5">
              <p className="font-heading text-4xl font-extrabold">20 +</p>
              <p className="mt-1 max-w-[140px] text-sm leading-snug text-[#C5DEEC]">
                Years of Trusted Service
              </p>
            </div>
          </div>

          <div data-reveal style={{ transitionDelay: "120ms" }}>
            <PillBadge>About Us</PillBadge>

            <h2 className="mt-4 font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-[#0B2748] sm:text-4xl lg:text-[42px]">
              Because Your{" "}
              <span className="text-[#0785C7]">Peace Matters</span>
            </h2>

            <p className="mt-5 text-[15.5px] leading-relaxed text-[#526A7D]">
              Life is full of challenges and unexpected events. Before
              anything unpredictable happens, protect yourself and your
              family from financial loss with the right insurance coverage.
              As your personal insurance advisor in Surrey BC, we take the
              worry and guesswork out of your financial protection.
            </p>

            <ul className="feature-checklist mt-7 space-y-3.5">
              {HOME_FEATURES.map((text) => (
                <li
                  key={text}
                  className="flex items-start gap-3 text-[15px] font-medium text-[#193B58]"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#0785C7]" />
                  {text}
                </li>
              ))}
            </ul>

            <div className="about-actions mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/about"
                className="btn-primary group"
                data-testid="about-read-more"
              >
                Read More
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>

              <ScheduleCallButton
                className="btn-outline"
                testId="about-schedule-call"
              />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="home-services-section bg-[#F4F7FB] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading
            badge="Our Services"
            title={
              <>
                Reliable Insurance,{" "}
                <span className="text-[#0785C7]">Anytime, Anywhere</span>
              </>
            }
            subtitle="Whether you were born in Canada, are new to the country, or are just visiting, we will find the right coverage for the right price."
          />

          <div className="mt-14 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {HOME_SERVICES.map((service, index) => (
              <ServiceCard
                key={service.slug}
                service={service}
                index={index}
                image={HOME_SERVICE_IMAGES[service.slug]}
              />
            ))}

            <div
              data-reveal
              style={{ transitionDelay: "400ms" }}
              className="group relative flex flex-col justify-between overflow-hidden rounded-2xl bg-[#006FB9] p-8 text-white shadow-[0_2px_16px_rgba(0,111,185,0.15)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_24px_48px_rgba(0,111,185,0.3)]"
            >
              <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#B9DFF3]/15 transition-transform duration-700 group-hover:scale-150" />

              <div className="relative">
                <h3 className="font-heading text-2xl font-bold leading-tight">
                  Not sure which coverage you need?
                </h3>

                <p className="mt-4 text-sm leading-relaxed text-[#C5DEEC]">
                  Book a free consultation. Our advisors are available 7
                  days a week.{" "}
                  <a href={HOME_PHONE_HREF}>Call {HOME_PHONE}.</a>
                </p>
              </div>

              <button
                type="button"
                onClick={() => openQuote()}
                className="btn-light relative mt-8 w-fit"
                data-testid="services-cta-quote"
              >
                Get Free Advice
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading
            badge="Why Choose Us"
            title={
              <>
                Insurance Made{" "}
                <span className="text-[#0785C7]">Easy and Honest</span>
              </>
            }
            subtitle="Twenty five years of answering every insurance question you can imagine, working with some of the most reliable insurance companies in British Columbia."
          />

          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {HOME_WHY_CHOOSE.map((item, index) => {
              const Icon = WHY_ICONS[item.icon];

              return (
                <div
                  key={item.title}
                  data-reveal
                  style={{ transitionDelay: `${index * 70}ms` }}
                  className="mobile-centered-card tilt-card group rounded-2xl border border-[#E1ECF3] bg-white p-8 transition-all duration-500 hover:border-transparent hover:shadow-[0_20px_44px_rgba(0,111,185,0.12)]"
                >
                  <span className="grid h-[52px] w-[52px] place-items-center rounded-2xl bg-[#E2F2FA] transition-all duration-500 group-hover:rotate-6 group-hover:bg-[#006FB9]">
                    <Icon className="h-6 w-6 text-[#006FB9] transition-colors duration-500 group-hover:text-[#B9DFF3]" />
                  </span>

                  <h3 className="mt-5 font-heading text-lg font-bold text-[#0B2748]">
                    {item.title}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#526A7D]">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CREDENTIALS MARQUEE */}
      <section className="border-y border-[#E1ECF3] bg-[#F4F7FB] py-6">
        <div className="marquee-mask overflow-hidden">
          <div className="marquee flex w-max items-center gap-4">
            {[...CREDENTIALS, ...CREDENTIALS].map((credential, index) => (
              <span
                key={index}
                className="flex items-center gap-2.5 whitespace-nowrap rounded-full bg-white px-6 py-2.5 text-sm font-semibold text-[#193B58] shadow-sm"
              >
                <Award className="h-4 w-4 text-[#0785C7]" />
                {credential}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ADVISOR */}
      <section className="home-advisor-section bg-white py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2">
          <div data-reveal>
            <PillBadge>Meet Your Advisor</PillBadge>

            <h2 className="mt-4 font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-[#0B2748] sm:text-4xl lg:text-[42px]">
              Professional Advice,{" "}
              <span className="text-[#0785C7]">Personal Care</span>
            </h2>

            <p className="mt-5 text-[15.5px] leading-relaxed text-[#526A7D]">
              {HOME_ADVISOR.bio}
            </p>

            <div className="advisor-highlights mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {HOME_ADVISOR.highlights.map((highlight) => (
                <span
                  key={highlight}
                  className="flex items-center gap-2.5 rounded-xl bg-[#F4F7FB] px-4 py-3 text-sm font-semibold text-[#193B58]"
                >
                  <Trophy className="h-4 w-4 shrink-0 text-[#0785C7]" />
                  {highlight}
                </span>
              ))}
            </div>

            <Link
              to="/contact"
              className="btn-primary group mt-9"
              data-testid="advisor-contact"
            >
              Book a Free Consultation
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>

          <div
            className="overflow-hidden rounded-3xl bg-[#F4F7FB] shadow-[0_12px_40px_rgba(6,59,103,0.12)]"
            data-reveal
            style={{ transitionDelay: "120ms" }}
          >
            <div className="overflow-hidden">
              <img
                src={HOME_ADVISOR.image}
                alt={HOME_ADVISOR.name}
                className="h-[420px] w-full object-cover object-top transition-transform duration-700 hover:scale-105 sm:h-[500px]"
              />
            </div>

            <div className="border-t border-[#E1ECF3] bg-white px-6 py-5 sm:px-8">
              <p className="font-heading text-lg font-bold text-[#0B2748]">
                {HOME_ADVISOR.name}
              </p>
              <p className="text-sm text-[#526A7D]">
                {HOME_ADVISOR.role}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* REDESIGNED SHARED TESTIMONIALS */}
      <HomeTestimonialsSection />

      {/* OUR LOCATION */}
      <HomeLocationSection />

      {/* MID CTA BAND */}
      <section className="desktop-section-shell desktop-feature-section home-protection-section relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/37877428/pexels-photo-37877428.jpeg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#062A4D]/80" />

        <div className="relative mx-auto max-w-4xl px-6 py-24 text-center">
          <div data-reveal>
            <PillBadge light>Abhi, Protecting You Always</PillBadge>

            <h2 className="mt-5 font-heading text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl">
              Ready to protect the people you love the most?
            </h2>

            <p className="mx-auto mt-5 max-w-xl text-[15.5px] leading-relaxed text-[#D4E9F4]">
              Speak with a licensed advisor today. Free consultation,
              honest advice, and coverage matched to your budget.
            </p>

            <button
              type="button"
              onClick={() => openQuote()}
              className="btn-light group mx-auto mt-9"
              data-testid="mid-cta-quote"
            >
              Request a Free Consultation
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="home-blog-section bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading
            badge="Our Blog"
            title={
              <>
                Insurance Insights,{" "}
                <span className="text-[#0785C7]">Made Simple</span>
              </>
            }
            subtitle="Plain language guides to help you make confident decisions about your coverage."
          />

          <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-3">
            {BLOG_POSTS.slice(0, 3).map((post, index) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                data-reveal
                style={{ transitionDelay: `${index * 90}ms` }}
                data-testid={`blog-card-${post.slug}`}
                className="tilt-card group overflow-hidden rounded-2xl bg-white shadow-[0_2px_16px_rgba(0,111,185,0.07)] transition-all duration-500 hover:shadow-[0_24px_48px_rgba(0,111,185,0.15)]"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3.5 py-1 text-xs font-bold text-[#006FB9]">
                    {post.category}
                  </span>
                </div>

                <div className="p-7">
                  <p className="text-xs font-medium text-[#70879A]">
                    {post.date} · {post.readTime}
                  </p>
                  <h3 className="mt-2.5 font-heading text-lg font-bold leading-snug text-[#0B2748] transition-colors duration-300 group-hover:text-[#0785C7]">
                    {post.title}
                  </h3>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#006FB9]">
                    Read Article
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-12 text-center" data-reveal>
            <Link
              to="/blog"
              className="btn-outline group"
              data-testid="view-all-posts"
            >
              View All Insights
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <div className="home-final-cta-wrap">
        <CTASection
          title="Secure What Matters Most"
          subtitle={
            <>
              Free consultations . No hidden fees. Your satisfaction is
              100% guaranteed.{" "}
              <a href={HOME_PHONE_HREF}>Call {HOME_PHONE}</a>
            </>
          }
        />
      </div>

     {/* SERVICE AND PAGE DISCLAIMER */}
<section
  aria-label="Service and page disclaimer"
  className="bg-[#F4F7FB] py-8 lg:py-10"
>
  <div className="mx-auto max-w-7xl px-6">
    <div className="rounded-xl border-l-[5px] border-r-[5px] border-[#0B4FAF] bg-white px-5 py-5 shadow-[0_8px_24px_rgba(11,39,72,0.08)] sm:px-7 lg:px-8">
      
      {/* Short disclaimer for mobile */}
      <p className="text-sm leading-relaxed text-[#526A7D] md:hidden">
        {MOBILE_PAGE_DISCLAIMER}
      </p>

      {/* Complete disclaimer for tablet and desktop */}
      <p className="hidden text-sm leading-relaxed text-[#526A7D] md:block">
        {HOME_DISCLAIMER}
      </p>
      
    </div>
  </div>
</section>
    </div>
  );
}